import { Component, inject, Input, OnInit } from '@angular/core'
import Order from '@client/Order'
import { CardComponent } from '@ui/Components/Card/Card.Component'
import { CardTitleComponent } from '@ui/Components/Card/CardTitle.Component'
import { CardContentComponent } from '@ui/Components/Card/CardContent.Component'
import { PaymentPlanInstalment } from '@client/PaymentPlanInstalment'
import { RaisedButtonComponent } from '@ui/Components/Form/Button/RaisedButton.Component'
import { TextInputComponent } from '@ui/Components/Form/TextInput/TextInput.Component'
import { DatePickerComponent } from '@ui/Components/Form/DatePicker/DatePicker.Component'
import { SystemConfigService } from '@client/Services/SystemConfigService'
import { NgStyle } from '@angular/common'
import { BaseCurrencyComponent } from '@ui/Components/Currency/BaseCurrency.Component'
import Payment from '@client/Payment'
import { BillingService } from '@client/Services/BillingService'
import { PaymentPlan } from '@client/PaymentPlan'
import HttpError, { FormErrors } from '@ui/HttpError'
import { MatSnackBar } from '@angular/material/snack-bar'
import { InitialPayment } from '@client/BillingRequest'

declare global {
	interface Window {
		initialPayment?: InitialPayment
	}
}

@Component({
	selector: 'create-payment-plan',
	templateUrl: './CreatePaymentPlan.Component.html',
	imports: [
		CardComponent,
		CardTitleComponent,
		CardContentComponent,
		RaisedButtonComponent,
		TextInputComponent,
		DatePickerComponent,
		NgStyle,
		BaseCurrencyComponent,
	],
})
export class CreatePaymentPlanComponent implements OnInit {
	@Input() protected readonly order: Order

	@Input() private readonly handleSuccess: (paymentPlan: PaymentPlan) => void

	protected formErrors: FormErrors = {}

	protected instalmentTotal: number = 0

	protected initialPaymentAmount: number = 0

	protected instalments: PaymentPlanInstalment[] = []

	protected isCreatingSchedule: boolean = false

	protected readonly systemConfigService: SystemConfigService

	private readonly billingService: BillingService

	private readonly snackbar: MatSnackBar

	constructor() {
		this.systemConfigService = inject(SystemConfigService)
		this.billingService = inject(BillingService)
		this.snackbar = inject(MatSnackBar)
	}

	public ngOnInit() {
		this.addInstalment()
		this.handleInitialAmount()
	}

	protected readonly addInstalment = () => {
		if (
			this.order &&
			this.order.total === this.initialPaymentAmount + this.instalmentTotal
		) {
			return
		}

		this.instalments.push(
			new PaymentPlanInstalment(undefined, undefined, new Date()),
		)

		const key = this.instalments.length - 1

		this.formErrors[this.makeKey(key, 'amount')] = ''
		this.formErrors[this.makeKey(key, 'due_date')] = ''
	}

	protected readonly createSchedule = async () => {
		if (
			this.order.total !== this.initialPaymentAmount + this.instalmentTotal ||
			this.isCreatingSchedule
		) {
			return
		}

		this.isCreatingSchedule = true

		try {
			const paymentPlan = new PaymentPlan(
				undefined,
				this.order.currency,
				undefined,
				undefined,
				this.instalmentTotal,
				this.instalments.map(
					(instalment: PaymentPlanInstalment): PaymentPlanInstalment => {
						return new PaymentPlanInstalment(
							undefined,
							parseFloat(instalment.amount.toString(10)) *
								10 ** this.order.currency_precision,
							instalment.due_date,
						)
					},
				),
			)

			const response = await this.billingService.createSchedule(
				this.order.id,
				paymentPlan,
				window.initialPayment,
			)

			this.handleSuccess(response)
		} catch (error) {
			if (error instanceof HttpError) {
				if (error.isUnprocessableEntity()) {
					this.formErrors = error.handleFormErrors(this.formErrors, '')

					this.snackbar.open(error.message, 'Close', {
						duration: 5000,
					})
				}
			} else {
				throw error
			}
		}

		this.isCreatingSchedule = false
	}

	protected readonly removeInstalment = (index: number) => {
		this.instalments.splice(index, 1)
		delete this.formErrors[this.makeKey(index + 1, 'amount')]
		delete this.formErrors[this.makeKey(index + 1, 'due_date')]
		this.calculateInstalmentTotal()
	}

	protected readonly calculateInstalmentTotal = (): void => {
		this.instalmentTotal = this.instalments.reduce(
			(total, instalment: PaymentPlanInstalment) => {
				const thisAmount = instalment.amount
					? instalment.amount.toString(10)
					: '0'
				return (
					total + parseFloat(thisAmount) * 10 ** this.order.currency_precision
				)
			},
			0,
		)
	}

	private readonly handleInitialAmount = () => {
		const initialPayment = window.initialPayment

		let initialPaymentInsideOrder = false

		let initialAmount = 0

		this.order.payments.forEach((payment: Payment) => {
			if (!payment.paid) {
				return
			}

			if (
				initialPayment &&
				initialPayment.request_id === payment.billing_request?.service_id
			) {
				initialPaymentInsideOrder = true
			}

			initialAmount += payment.amount
		})

		if (!initialPaymentInsideOrder && initialPayment) {
			initialAmount += initialPayment.amount
		}

		this.initialPaymentAmount = initialAmount

		if (this.initialPaymentAmount === this.order.total) {
			const paymentPlan = new PaymentPlan(
				undefined,
				this.order.currency,
				undefined,
				undefined,
				0,
				[],
			)

			setTimeout(() => {
				this.handleSuccess(paymentPlan)
			})
		}
	}

	private readonly makeKey = (index: number, key: string) => {
		return `planinstalments.${index}.${key}`
	}
}
